import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import api from '@/axios/api'

import { handleApiError } from '@scripts/modules/handleApiError'
import { LanguageEnum } from '@models/entities/languages'
import {
  LocalizationRequestPendingEnum,
  type LocalizationRequestPendingsType
} from '@models/requests/pendings'

import { CsvToArray } from '@scripts/modules/parseCsv'

export const useLocalizationStore = defineStore('useLocalizationStore', () => {
  const isReceivedDefaultLanguageTranslations = ref(import.meta.env.MODE === 'development')
  const translations = ref<Record<LanguageEnum, Record<string, string>>>(
    {} as Record<LanguageEnum, Record<string, string>>
  )
  const requestPendings = ref<LocalizationRequestPendingsType>({
    [LocalizationRequestPendingEnum.getDefaultLanguageTranslationsPending]: false
  } as LocalizationRequestPendingsType)

  const getTranslatedLanguages = computed<LanguageEnum[]>(
    () => Object.keys(translations.value) as LanguageEnum[]
  )

  const setTranslations = (value: Record<LanguageEnum, Record<string, string>>) => {
    translations.value = value
  }

  const setLanguageTranslations = (csvData: string) => {
    const csvArray = CsvToArray(csvData)
    const languageKey = csvArray.shift()![0] as LanguageEnum

    const languageTranslations = csvArray.reduce((acc, cur) => {
      acc[cur[0]] = cur[1]
      return acc
    }, {} as Record<string, string>)

    setTranslations({
      ...translations.value,
      [languageKey]: languageTranslations
    })
  }

  const setRequestPending = (
    requestPending: LocalizationRequestPendingEnum,
    status: boolean = true
  ) => {
    requestPendings.value[requestPending] = status
  }

  const getDefaultLanguageTranslations = async (url: string) => {
    try {
      const response = await api.get(url)
      setLanguageTranslations(response.data)
      isReceivedDefaultLanguageTranslations.value = true
    } catch (error) {
      handleApiError(error)

      if (!requestPendings.value[LocalizationRequestPendingEnum.getDefaultLanguageTranslationsPending]) {
        setRequestPending(LocalizationRequestPendingEnum.getDefaultLanguageTranslationsPending)

        setTimeout(() => {
          setRequestPending(LocalizationRequestPendingEnum.getDefaultLanguageTranslationsPending, false)
          getDefaultLanguageTranslations(url)
        }, 5000)
      }
    }
  }

  const getAvailableLanguagesTranslations = async (url: string) => {
    try {
      const response = await api.get(url)
      setLanguageTranslations(response.data)
    } catch (error) {
      handleApiError(error)
    }
  }

  return {
    isReceivedDefaultLanguageTranslations,
    translations,
    requestPendings,
    getTranslatedLanguages,
    setTranslations,
    setLanguageTranslations,
    setRequestPending,
    getDefaultLanguageTranslations,
    getAvailableLanguagesTranslations
  }
})
